module Sm.UI.Main

open Browser
open MobF

open Sm.Core.Numeric

open Sm.Service.App
open Sm.Service.Session
open Sm.Service.Remote
open Sm.Service.Remote.Synchronizer

open Sm.UI.Interaction

type State = {
    User: User
    Menu: Menu.Type
    Interaction: Interaction
    Timer: Timer.TimerModel
    Pad: Pad.Pad
    Autotimer: Autotimer.Autotimer
}

type Msg =
    | Tick
    | HandleCommand of Command
    | Reload

type ProgressBar = {
    Text: string
    Subtext: string
    Value: float
}

type IComputed =
    abstract SyncStatus: Synchronizer.Status
    abstract Progress: ProgressBar option
    abstract ReloadMessage: string option

type Type = Model<State, Msg, IComputed>

let create ctx session : Type =
    let backgroundTimer = Clock.backgroundTimer()
    let clock = Clock.create ()

    let init post =
        backgroundTimer.Tick <- fun () ->
            post Tick

        let interaction = Interaction.create ()
        let timer = Timer.create ctx

        timer |> Subscribe.auto
            (fun state -> state.Activity)
            (function
                | Some { Running = true } ->
                    interaction.Post(Lock)
                    backgroundTimer.Start(Clock.Seconds)
                | _ ->
                    interaction.Post(Release)
                    backgroundTimer.Start(Clock.Minutes)
            )

        backgroundTimer.Start(Clock.Minutes)

        let pad = Pad.create interaction ctx.Repository
        timer.Post(Timer.ProvideStepSource pad.Computed)

        {
            User = ctx.User
            Menu = Menu.create ctx session
            Interaction = interaction
            Timer = timer
            Pad = pad
            Autotimer = Autotimer.create timer pad
        }

    let update state = function
        | Tick ->
            clock.Post(Clock.Tick)
            state.Timer.Post(Timer.Tick)
            state.Pad.Post(Pad.Tick)
            session.Post(Sm.Service.Session.Tick)
            state

        | HandleCommand cmd ->
            match cmd with
            | TimerStart | TimerStop -> state.Timer.Post(Timer.HandleCommand cmd)
            state

        | Reload ->
            window.location.reload()
            state

    let compute _ =
        { new IComputed with
            member _.SyncStatus =
                ctx.Synchronizer.Computed.Status

            member this.Progress =
                let percent total current =
                    if total = 0 || current = 0 then 100.
                    else
                        (current /. total) *. 100
                        |> max 0
                        |> min 100

                match this.SyncStatus with
                | Running (Sending (total = t; current = c)) when t >= 100 ->
                    Some {
                        Text = "Sync"
                        Subtext = "Local Changes";
                        Value = percent t c 
                    }
                | Running (Receiving (total = t; current = c)) when t >= 100 ->
                    Some {
                        Text = "Sync"
                        Subtext = "Remote Changes";
                        Value = percent t c 
                    }
                | _ -> None

            member _.ReloadMessage =
                match reloadSwitch.State with
                | Off -> None
                | On msg -> Some msg
        }

    Model.useInitWithPost init
    |> Model.andUpdate update
    |> Model.createWithComputed compute

let registerKeyHandlers post (mousetrap: Mousetrap.IMousetrap) =
    mousetrap.stopCallback <- fun e element command ->
        if element.matches("textarea") then
            match command with
            | "tab" | "f5" | "f8" -> false
            | _ -> true
        else
            mousetrap.invokeDefaultStopCallback e element command

    let bind key msg =
        mousetrap.bind([|key|], fun _ _ -> post msg; false)

    bind "f5" TimerStart
    bind "f8" TimerStop
